import FileUpload from "components/FileUpload";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import MainLayout from "layouts/MainLayout";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { addSuspectToWitness } from "services/caseServices";
import { fetchSuspect } from "services/lealIntityService";
import { CaseCreation, Suspect, SuspectTowitness } from "types";
import { errorHandlerOnForm } from "utils/helper";

const defaultValue: SuspectTowitness = {
  crime_registration_number: "",
  incident_registration_number: "",
  prosecutor_registration_number: "",
  suspect: "",
  agreement_with_suspect: false,
  agreement_with_suspect_file: null,
  request_date: null,
  decision: "Pending",
  conversion_summary: "",
  immunity_revoked: false,
  revocation_reason: "",
  revoked_by_attorney_general: false,
  prosecutor_name: "",
  prosecutor_user_id: "",
  org_unit_id: "",
  admin_unit_id: "",
  status: "Pending",
  revocation_decision: null,
};

const SuspectToWitnessRequest: React.FC = () => {
  const { state } = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const caseData: CaseCreation | null = state?.caseData || null;
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  // const [tab, setTab] = useState("Suspect Information");

  const [suspects, setSuspects] = useState<Suspect[]>([]);
  // const [filteredSusect, setFilteredSusect] = useState<Suspect[]>([]);

  const [formState, setFormState] = useState<SuspectTowitness>(defaultValue);

  useEffect(() => {
    fetchSuspect()
      .then((data) => {
        setSuspects(data);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (loginData) {
      setFormState((prevState) => ({
        ...prevState,
        prosecutor_user_id: String(loginData?.user_id ?? ""),
        org_unit_id: String(loginData?.org_unit ?? ""),
        admin_unit_id: String(loginData?.admin_unit ?? ""),
        prosecutor_name: String(loginData?.full_name ?? ""),
      }));
    }
  }, [loginData]);

  const handleFile = (name: string, file: File | null) => {
    setFormState({ ...formState, [name]: file });
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formState?.suspect) return "Suspect is Required";
    else if (!formState.crime_registration_number)
      return "Crime Registration Number is Required.";
    else if (!formState.incident_registration_number)
      return "Incident Registration Number is Required.";
    else if (!formState.prosecutor_registration_number)
      return "Prosecutor Registration Number is Required.";
    else if (!formState.agreement_with_suspect_file)
      return "Agreement With Suspect File is Required.";
    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }
    const formData: SuspectTowitness = {
      ...formState,
      decision: "Pending",
      status: "forwarded_to_cordinator",
    };
    addSuspectToWitness(formData)
      .then(() => {
        showTooltip(t("successfully_submitted"), "success");
        navigate("/suspect-to-witness");
      })
      .catch((err) => {
        showTooltip(errorHandlerOnForm(err?.response?.data), "error");
      });
  };

  return (
    <MainLayout>
      <div className="incidentRegistration max-w-screen-lg">
        <div className="incident-title">
          <span className="text-2xl font-semibold text-gray-700">
            ⚖ Suspect to Witness
          </span>
        </div>
        <div className="details mb-4">
          <p>
            Lorem Ipsum is a placeholder text commonly used in the design...
          </p>
        </div>
        <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
          <form onSubmit={handleSubmit}>
            <div className="bg-white shadow-md rounded-lg p-6 mt-4 ">
              {/* Suspect Information */}
              <div>
                <div className="flex items-center mt-4 w-[678px]">
                  <p className="text-[#9D0303] font-[Poppins] font-[600] text-lg cursor-pointer">
                    Suspect Information
                  </p>
                  <hr className="flex-1 border-t border-[#E9E9E9] ml-4" />
                </div>
                <div className={"block"}>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    {/* Select Suspect (first) */}
                    <div className="flex flex-col mt-4 col-span-2">
                      <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                        Select Suspect
                      </label>
                      <select
                        className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
                        value={formState?.suspect ?? ""}
                        name="suspect"
                        onChange={(e) => {
                          const selectedId = e.target.value
                            ? e.target.value
                            : "";
                          const selectedSuspect =
                            suspects.find(
                              (s) => String(s.uu_id) === String(selectedId),
                            ) || null;

                          setFormState({
                            ...formState,
                            suspect: selectedId,
                            crime_registration_number:
                              selectedSuspect?.CR_number || "",
                            incident_registration_number:
                              selectedSuspect?.Incident_Registration_number ||
                              "",
                          });
                        }}
                      >
                        <option value={""}>Select Suspect</option>
                        {suspects.map((suspect, index) => (
                          <option key={index} value={suspect?.uu_id}>
                            {suspect?.first_name} {suspect?.middle_name}{" "}
                            {suspect?.last_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    {/* Crime Registration Number */}
                    <div className="flex flex-col mt-4">
                      <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                        Crime Registration Number
                      </label>
                      <input
                        type="text"
                        placeholder="#123456"
                        className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                        name="crime_registration_number"
                        value={formState?.crime_registration_number ?? ""}
                        onChange={(e) => {
                          setFormState({
                            ...formState,
                            crime_registration_number: e.target.value,
                          });
                        }}
                        // disabled
                      />
                    </div>
                    {/* Incident Registration Number */}
                    <div className="flex flex-col mt-4">
                      <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                        Incident Registration Number
                      </label>
                      <input
                        type="text"
                        placeholder="#123456"
                        className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                        name="incident_registration_number"
                        value={formState?.incident_registration_number ?? ""}
                        onChange={(e) => {
                          setFormState({
                            ...formState,
                            incident_registration_number: e.target.value,
                          });
                        }}
                        disabled
                      />
                    </div>
                    {/* Prosecutor Registration Number */}
                    <div className="flex flex-col mt-4">
                      <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                        Prosecutor Registration Number
                      </label>
                      <input
                        type="text"
                        placeholder="#123456"
                        className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                        name="prosecutor_registration_number"
                        value={formState?.prosecutor_registration_number ?? ""}
                        onChange={handleChange}
                      />
                    </div>
                    {/* Agreement With Suspect */}
                    <div className="flex flex-col mt-4">
                      <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                        Agreement With Suspect
                      </label>
                      <select
                        className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
                        name="agreement_with_suspect"
                        value={
                          formState?.agreement_with_suspect ? "true" : "false"
                        }
                        onChange={(e) =>
                          setFormState({
                            ...formState,
                            agreement_with_suspect: e.target.value === "true",
                          })
                        }
                      >
                        <option value="false">No</option>
                        <option value="true">Yes</option>
                      </select>
                    </div>
                    {/* Request Date */}
                    <div className="flex flex-col mt-4">
                      <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                        Request Date
                      </label>
                      <input
                        type="date"
                        className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                        name="request_date"
                        value={
                          formState?.request_date
                            ? typeof formState.request_date === "string"
                              ? formState.request_date.substring(0, 10)
                              : ""
                            : ""
                        }
                        onChange={handleChange}
                      />
                    </div>
                    {/* Decision */}
                    <div className="flex flex-col mt-4">
                      <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                        Decision
                      </label>
                      <select
                        className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
                        name="decision"
                        value={formState?.decision ?? "Pending"}
                        disabled
                      >
                        <option value="Pending">Pending</option>
                      </select>
                    </div>

                    <div className="flex flex-col mt-4 col-span-2">
                      <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                        Conversion Summary
                      </label>
                      <textarea
                        className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                        name="conversion_summary"
                        value={formState?.conversion_summary ?? ""}
                        onChange={handleChange}
                        rows={4}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col mt-4 col-span-2">
                    <FileUpload
                      label="Agreement With Suspect File"
                      fileTypes="*"
                      required={true}
                      onChange={(file) =>
                        handleFile("agreement_with_suspect_file", file)
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-row mt-4 gap-3">
              {!caseData && (
                <input
                  type="reset"
                  className="border border-[#212121] px-[76px] py-3 text-[#747474] font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#D4D4D4]"
                  value={"Cancel"}
                />
              )}
              <input
                type="submit"
                className="border border-[#212121] bg-[#212121] px-[76px] py-3 text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]"
                value={caseData ? "Save" : "Finish"}
              />
            </div>
          </form>
        </div>
      </div>
    </MainLayout>
  );
};

export default SuspectToWitnessRequest;
